/**
 * CPV-G25 S11–S13 · the small pieces the data sheet screens share, read from the S7 API (`services/cpv/sheet.py`):
 * the act words of `allowed_acts`, the sheet's stage (its lifecycle projection), the release chip, the entry flags
 * (icon + word), the limits mini-bar of a line, the signed-act dialog, and what the embedded lifecycle read says about
 * a section (submitted / verified / returned, who is waited on, the due date).
 *
 * Every code is the server's — business state and stage, section, verdict and band, disposition, the acts allowed
 * now — and each is only given a word and a tone here (tokens.js). Nothing is judged: a flag is shown because the
 * server's entry carries it.
 */
import React from 'react';
import { Space, Tooltip, Typography } from 'antd';
import { Chip, DISPOSITION_TONE, FLAG_TONE, LimitsBar, NONE_TEXT, SECTION_STATE_TONE, SECTION_TONE, SHEET_STATUS_TONE, StatusPill, StatusText,
  limitRangeText } from '@/components/cpv-ui';
import SignedActionModal from '@/components/SignedActionModal';
import { words } from './CpvSignals';

const { Text } = Typography;

export const has = (v) => v !== null && v !== undefined && v !== '';
export const SECTIONS = ['PRODUCTION', 'QC', 'ENGINEERING'];
export const HEADER = 'HEADER';

/** The act words of `allowed_acts` (services/cpv/sheet.py ACT_*). */
export const ACT = {
  ISSUE: 'issue', ENTER: 'enter', SUBMIT: 'submit', VERIFY: 'verify', RETURN: 'return', APPROVE: 'approve', VOID: 'void',
  REISSUE: 'reissue', CORRECT: 'correct', CORRECTION_APPROVE: 'correction_approve', CORRECTION_DECLINE: 'correction_decline', PRINT: 'print', REPRINT: 'reprint',
};
const IN_WORKFLOW = 'IN_WORKFLOW';
const EFFECTIVE = 'EFFECTIVE';
const DONE = 'DONE';
const ACTIVE = 'ACTIVE';

/** What the source reference of a line is, by who records it (the paper the value is copied from). */
export const SOURCE_REF_LABEL = { PRODUCTION: 'BMR page/step', QC: 'AR / test report no.', ENGINEERING: 'Log ref' };
export const sourceRefLabel = (line) => (line.kind === HEADER ? SOURCE_REF_LABEL.PRODUCTION : SOURCE_REF_LABEL[line.section] || 'Source reference');

export const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
/** A stamp's time without the year (the full time is in its tooltip). */
export const whenShort = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
export const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
/** A sheet's number with its issue beside it (OD-4). */
export const sheetNo = (s) => (s ? `${s.number} / ${s.issue_no}` : '');

/**
 * The sheet's stage as the server projects it: voided; approved (business state EFFECTIVE); else the current lifecycle
 * stage, named by the server (`stages`: [{stage_key, name}] from the counts or the lifecycle read).
 */
export function stageOf({ voided, business_state, current_stage_key }, stages = []) {
  if (voided) return { code: 'VOIDED', label: 'Voided' };
  if (business_state === EFFECTIVE) return { code: 'APPROVED', label: 'Approved' };
  if (business_state === IN_WORKFLOW && current_stage_key) {
    const s = stages.find((x) => x.stage_key === current_stage_key);
    return { code: current_stage_key, label: s?.name || words(current_stage_key) };
  }
  return { code: business_state || '', label: words(business_state) || '—' };
}

export function SheetStatus({ stage, testId }) {
  if (!stage?.label) return null;
  return <StatusPill tone={SHEET_STATUS_TONE[stage.code] || 'neutral'} icon testId={testId}>{stage.label}</StatusPill>;
}

export function SectionChip({ section }) {
  if (!section) return null;
  return <Chip tone={SECTION_TONE[section] || 'neutral'} title="Section">{words(section)}</Chip>;
}

/** The batch's current disposition (a disposition dict, or its word), or "Not released". */
export function ReleaseChip({ release, testId }) {
  const d = typeof release === 'string' ? release : release?.disposition;
  if (!d) return <StatusText tone="neutral" muted label="Not released" testId={testId} />;
  const at = typeof release === 'object' ? release.occurred_at : null;
  return <StatusText tone={DISPOSITION_TONE[d] || 'neutral'} label={words(d)} tooltip={at ? `${words(d)} ${when(at)}` : undefined} testId={testId} />;
}

/**
 * The batch disposition as read only: "Released by <name>, <date>, at QA approval" when a data sheet's QA approval decided
 * it (the server's `approval`: {by, at}); else the disposition, its date and source (an import, or a separate act recorded
 * before the approval decided it); else "Not released".
 */
export function DispositionDecided({ disposition, approval, testId }) {
  if (!disposition?.disposition) return <StatusText tone="neutral" muted label="Not released" testId={testId} />;
  const word = words(disposition.disposition);
  if (approval) {
    return <StatusText tone={DISPOSITION_TONE[disposition.disposition] || 'neutral'} label={`${word} by ${nameOf(approval.by)}, ${when(approval.at)}, at QA approval`} testId={testId} />;
  }
  return <StatusText tone={DISPOSITION_TONE[disposition.disposition] || 'neutral'} label={`${word} ${when(disposition.occurred_at)}${disposition.source ? ` · ${words(disposition.source)}` : ''}`} testId={testId} />;
}

/** Verdicts that carry no flag on the line (inside every limit, or no limit to judge by). */
const QUIET = ['IN_RANGE', 'NO_LIMIT'];
const FLAG_WORD = { OOS: 'OOS', ACTION: 'Action limit', ALERT: 'Alert limit', CONTROL: 'Out of control' };
/**
 * The server's flags of an entry, outermost first, from its verdict and band as stored (verdict OUT_OF_RANGE → OOS;
 * band ACTION / ALERT / CONTROL → that band); IN_RANGE and NO_LIMIT show no flag. No limit is compared here.
 */
export function flagsOf(entry) {
  if (!entry) return [];
  if (Array.isArray(entry.flags)) return entry.flags.filter((f) => !QUIET.includes(f));
  const out = [];
  if (entry.verdict === 'OUT_OF_RANGE') out.push('OOS');
  if (['ACTION', 'ALERT', 'CONTROL'].includes(entry.band)) out.push(entry.band);
  return out;
}
export const isOos = (entry) => flagsOf(entry).includes('OOS');

export function Flags({ entry, testId }) {
  const flags = flagsOf(entry);
  if (!flags.length) return null;
  return (
    <span className="cs-flags" data-testid={testId}>
      {flags.map((f) => <StatusText key={f} tone={FLAG_TONE[f] || 'neutral'} label={FLAG_WORD[f] || words(f)} />)}
    </span>
  );
}

/**
 * A decimal as the server sends it (a string such as "20.0000000000"), shown to the line's configured precision — the
 * `decimals` the server copies onto the line from the parameter master (none for a header field or an attribute).
 *  - `fixed`: padded to that many places (a recorded value: 11.3, 2.40, 150); with `round: false` a recorded value
 *    is never rounded — any digits beyond the decimals stay (11.2749 at 1 dp), only trailing zeros go;
 *  - otherwise trailing zeros are dropped;
 *  - `round`: rounded to that many places first.
 * A limit is not printed here: `limitParts` prints the server's `display` (Q4i, the shared formatter).
 * Rounding is half away from zero on the decimal string itself — no binary float is involved.
 */
export function numText(v, decimals, { fixed = false, round = fixed } = {}) {
  if (!has(v)) return '';
  const raw = String(v).trim();
  // a decimal may arrive in exponent form ("0E-10", "1.5E+3"): the point is moved, digit for digit
  const m = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(raw);
  if (!m || (!m[2] && !m[3])) return raw;
  const sign = m[1];
  let int = m[2] || '0';
  let frac = m[3] || '';
  const exp = Number(m[4] || 0);
  if (exp > 0) { frac = frac.padEnd(exp, '0'); int += frac.slice(0, exp); frac = frac.slice(exp); }
  if (exp < 0) { int = int.padStart(-exp + 1, '0'); frac = int.slice(int.length + exp) + frac; int = int.slice(0, int.length + exp); }
  const dp = Number.isInteger(decimals) && decimals >= 0 ? decimals : null;
  if (dp !== null && round && frac.length > dp) {
    const digits = (int + frac.slice(0, dp)).split('').map(Number);
    if (Number(frac[dp]) >= 5) {
      let i = digits.length - 1;
      while (i >= 0 && digits[i] === 9) { digits[i] = 0; i -= 1; }
      if (i < 0) digits.unshift(1); else digits[i] += 1;
    }
    const all = digits.join('');
    int = all.slice(0, all.length - dp);
    frac = all.slice(all.length - dp);
  }
  // fixed: padded to the decimals; digits beyond them (not rounded away) stay, less their trailing zeros
  frac = fixed && dp !== null ? frac.slice(0, dp).padEnd(dp, '0') + frac.slice(dp).replace(/0+$/, '') : frac.replace(/0+$/, '');
  int = int.replace(/^0+(?=\d)/, '');
  const out = frac ? `${int}.${frac}` : int;
  return sign === '-' && /[1-9]/.test(out) ? `-${out}` : out;
}

/**
 * The limits a line was issued with (copied at issue): spec, action, alert, control — each as stated, never computed,
 * shown to the line's precision (the stated ones less their trailing zeros, the computed control limits rounded).
 */
export function limitParts(line) {
  const { spec = {}, action = {}, alert = {}, control = {} } = line;
  // Q4i: the server's text (services/cpv/limit_text) — the line's decimals, trailing zeros kept; a control limit one
  // place more (the plant's setting); a line read before the server carried it is printed by the shared formatter
  const d = line.display || {};
  const places = has(d.decimals) ? d.decimals : line.decimals;
  const text = (key, lo, hi, at) => (has(d[key]) ? (d[key] === NONE_TEXT ? null : d[key])
    : (has(lo) || has(hi) ? limitRangeText(lo, hi, at) : null));
  return [
    ['Spec', text('spec', spec.lsl, spec.usl, places)],
    ['Action', text('action', action.lower, action.upper, places)],
    ['Alert', text('alert', alert.lower, alert.upper, places)],
    ['Control', text('control', control.lcl, control.ucl, has(d.limit_decimals) ? d.limit_decimals : places)],
  ].filter(([, t]) => t);
}

/**
 * The limits of a line: the bar (spec band, control band, action / alert ticks, the value marker). `compact` (the dense
 * grid) shows the first stated range as text beside the bar and every range in its tooltip; otherwise each range is
 * listed under the bar (touch layout).
 */
export function LineLimits({ line, value, tone, testId, compact = false }) {
  const parts = limitParts(line);
  if (!parts.length) return <span className="cu-muted cu-small">No limits</span>;
  const { spec = {}, action = {}, alert = {}, control = {} } = line;
  const bar = (
    <LimitsBar spec={{ lo: spec.lsl, hi: spec.usl }} control={{ lo: control.lcl, hi: control.ucl }} value={value} tone={tone}
      ticks={[{ at: action.lower, tone: 'warning' }, { at: action.upper, tone: 'warning' }, { at: alert.lower, tone: 'caution' }, { at: alert.upper, tone: 'caution' }]} />
  );
  if (!compact) {
    return (
      <div className="cs-limits" data-testid={testId}>
        {bar}
        <div className="cs-limits-text">{parts.map(([k, t]) => <span key={k}><span className="cu-muted">{k}</span> {t}</span>)}</div>
      </div>
    );
  }
  const [first, firstText] = parts[0];
  const unit = line.uom ? ` ${line.uom}` : '';
  return (
    <Tooltip title={<div className="cs-limits-tip">{parts.map(([k, t]) => <div key={k}><span>{k}</span> {t}{unit}</div>)}</div>}>
      <div className="cs-limits cs-limits--compact" data-testid={testId} tabIndex={0} role="img" aria-label={parts.map(([k, t]) => `${k} ${t}${unit}`).join('; ')}>
        {bar}
        <span className="cs-limits-first">{first === 'Spec' ? null : <span className="cu-muted">{first} </span>}{firstText}{unit}</span>
      </div>
    </Tooltip>
  );
}

// ------------------------------------------------------------------ the embedded lifecycle read
/** A person as the server names one (`{id, name}`: entered_by, actor, printed_by, issued_by, void.by, approval.by). */
export const nameOf = (p) => p?.name || '—';

const stepsWhere = (lc, pred) => (lc?.stages || []).flatMap((st) => (st.steps || []).map((sp) => ({ ...sp, stage_key: st.stage_key, stage_kind: st.kind, stage: st }))).filter(pred);
/** The esign id of a step's latest act (the lifecycle history). */
const esignOf = (lc, step) => [...(lc?.history || [])].reverse().find((h) => h.step_key === step.step_key && h.stage_key === step.stage_key)?.esign_id ?? null;

/**
 * What the lifecycle read says about one section (the S7 detail tags each step with its section): its entry step and
 * its verification step (done by whom, when, under which meaning), the stage's last return, the open steps' roles and
 * their SLA (`lc.sla`).
 */
export function sectionFacts(lc, section) {
  const steps = stepsWhere(lc, (sp) => sp.section === section);
  const pick = (kind) => steps.find((sp) => sp.stage_kind === kind);
  const asSign = (sp) => (sp && sp.state === DONE && sp.by ? { name: sp.by.name, at: sp.at, meaning: sp.esign_meaning, signature_id: esignOf(lc, sp) } : null);
  const entry = pick('WORK');
  const verify = pick('DECISION');
  const open = steps.filter((sp) => sp.state === ACTIVE);
  const ret = verify?.stage?.last_return?.step_key === verify?.step_key ? verify.stage.last_return : entry?.stage?.last_return || null;
  const sla = (lc?.sla || []).filter((x) => open.some((o) => o.step_key === x.step_key && o.stage_key === x.stage_key));
  return { submitted: asSign(entry), verified: asSign(verify), returned: ret, waiting: open.flatMap((o) => (o.roles || []).map((r) => r.name)).filter(Boolean), sla };
}

const STATE_WORD = { RETURNED: 'Returned', VERIFIED: 'Verified', SUBMITTED: 'Submitted', OVERDUE: 'Overdue', OPEN: 'Open' };
/**
 * A section's state from what the lifecycle read says about it (`sectionFacts`): verified, submitted, returned (its
 * step reopened), overdue (an open step past the SLA the server states), else open — a word and a tone for the tab dot.
 */
export function sectionState(f) {
  const code = f?.verified ? 'VERIFIED' : f?.submitted ? 'SUBMITTED' : f?.returned ? 'RETURNED' : (f?.sla || []).some((x) => x.overdue) ? 'OVERDUE' : 'OPEN';
  return { code, word: STATE_WORD[code], tone: SECTION_STATE_TONE[code] };
}

/** The signature of the step of no section at a stage (the QA approval), as the lifecycle read states it. */
export function stageSignature(lc, stageKind = 'DECISION') {
  const sp = stepsWhere(lc, (x) => !x.section && x.stage_kind === stageKind && x.state === DONE && x.by).at(-1);
  return sp ? { name: sp.by.name, at: sp.at, meaning: sp.esign_meaning, signature_id: esignOf(lc, sp) } : null;
}

/** The open steps of no section (issue, QA approval) and who is waited on there. */
export function openUnsectioned(lc) {
  return stepsWhere(lc, (sp) => !sp.section && sp.state === ACTIVE);
}

/**
 * A signed sheet act: the meaning the server bound to it (when it states one) is shown before the credentials are
 * entered and sent back as `meanings_shown`; the remarks are the signature's reason.
 */
export function SignActModal({ open, title, meaning, intent = 'primary', confirmText = 'Sign', busy, error, onCancel, onSubmit, children, reasonLabel = 'Remarks' }) {
  return (
    <SignedActionModal open={open} alwaysSign intent={intent} title={title} confirmText={confirmText} submitting={busy} error={error}
      reasonLabel={reasonLabel} reasonPlaceholder="What are you signing, and why? It is written with the signature."
      context={(
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div data-testid="cs-meaning">
            {meaning
              ? <><Text strong>You are signing one meaning:</Text><ul style={{ margin: '4px 0 0', paddingLeft: 18 }}><li>Meaning: “{meaning}”</li></ul></>
              : <Text>The server records this signature under the step's own meaning.</Text>}
          </div>
          {children}
        </Space>
      )}
      onCancel={onCancel} onSubmit={onSubmit} />
  );
}
